import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import moment from "moment";
import {
  Button,
  Container,
  Divider,
  Form,
  Icon,
  Label,
  Message,
  Modal,
  ModalActions,
  ModalContent,
  Table,
} from "semantic-ui-react";

import { OpenAPI } from "simplydo/interfaces";
import { Input } from "simplydo/react/component-library";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import { EnableMfa, SuggestedMFAApps } from "components/lib/EnableMfa";
import PasswordGuidance from "components/lib/Entry/PasswordGuidance";

type Token = NonNullable<
  OpenAPI.GET<"/accounts/api/tokens">["response"]["tokens"]
>[number];

export const UserSettingsSecurity = () => {
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const onDisabledMfa = useCallback(() => {
    dispatch(actions.user.disableMfa());
  }, [dispatch]);
  const onEmailAddressChanged = useCallback(
    (email: string) => dispatch(actions.user.updateEmailAddress(email)),
    [dispatch],
  );

  const [emailError, setEmailError] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [working, setWorking] = useState(false);
  const [showPasswordOld, setShowPasswordOld] = useState(false);
  const [showPasswordNew, setShowPasswordNew] = useState(false);
  const [mfa, setMfa] = useState(user.mfaEnabled);
  const [enableMfaModalOpen, setEnableMfaModalOpen] = useState(false);
  const [disableMfaModel, setDisableMfaModel] = useState(false);
  const [mfaPassword, setMfaPassword] = useState("");
  const [loggingOutOthers, setLoggingOutOthers] = useState(false);
  const [apiTokens, setApiTokens] = useState<Token[]>([]);
  const [loggingOutToken, setLogginOutToken] = useState("");

  useEffect(() => {
    api.auth.getApiTokens(
      (newTokens) => {
        setApiTokens(newTokens.tokens);
      },
      () => {},
    );
  }, []);

  const saveEmail = useCallback(() => {
    setEmailError("");
    setEmailLoading(true);
    api.auth.updateEmailAddress(
      { email: newEmail, sendComms: true },
      ({ email }) => {
        setEmailLoading(false);
        setNewEmail("");
        onEmailAddressChanged(email);
        toast("Email address successfully updated");
      },
      (err) => {
        setEmailLoading(false);
        setEmailError(err.message);
      },
    );
  }, [newEmail, onEmailAddressChanged]);

  const updatePassword = () => {
    setWorking(true);
    api.auth.updatePassword(
      currentPassword,
      newPassword,
      () => {
        toast.success(t("users.account.options.changePassword.success"));
        setCurrentPassword("");
        setNewPassword("");
        setWorking(false);
        setErrorMessage("");
      },
      (err) => {
        setWorking(false);
        setErrorMessage(err.message);
      },
    );
  };

  const toggleMfa = useCallback(() => {
    const shouldToggle = !mfa;
    if (shouldToggle) {
      setEnableMfaModalOpen(true);
    } else {
      setDisableMfaModel(true);
    }
  }, [mfa]);

  const disableMfa = () => {
    api.auth.disableOtp(
      { password: mfaPassword },
      () => {
        toast.success(t("entry.mfa.statusMessageDisabledSuccess"));
        setMfa(false);
        onDisabledMfa();
      },
      (err) => {
        toast.error(err.message);
      },
    );
    setDisableMfaModel(false);
    setMfaPassword("");
  };

  const logoutToken = (tokenId: string, index: number) => {
    util
      .confirm(
        "Log out this device?",
        "To access your account again on this device you will need to re-authenticate.",
      )
      .then(() => {
        setLogginOutToken(tokenId);
        api.auth.deleteApiToken(
          tokenId,
          index,
          () => {
            setLogginOutToken("");
            if (tokenId) {
              setApiTokens(
                apiTokens.filter((tokenData) => tokenData.id !== tokenId),
              );
            } else {
              const newTokens = [...apiTokens];
              newTokens.splice(index, 1);
              setApiTokens(newTokens);
            }
          },
          (err) => {
            setLogginOutToken("");
            toast.error(err.message);
          },
        );
      })
      .catch(() => {});
  };

  const logoutOthers = () => {
    setLoggingOutOthers(true);
    util
      .confirm(
        "Log out all other devices?",
        "To access your account again on those devices you will need to re-authenticate.",
      )
      .then(() => {
        api.auth.logoutOthers(
          () => {
            setLoggingOutOthers(false);
            setApiTokens(
              apiTokens.filter((tokenData) => tokenData.token === api.token),
            );
            toast(t("users.account.options.logOut.success"));
          },
          (err) => {
            toast.error(err.message);
            setLoggingOutOthers(false);
          },
        );
      })
      .catch(() => {
        setLoggingOutOthers(false);
      });
  };

  return (
    <Container>
      <h3>{t("users.profile.communication.email.title")}</h3>
      <p>{t("users.profile.communication.email.info2")}</p>

      {user.ownerOrganisation?.preventEmailChange ?
        <Message>
          Your avatar was automatically set from your single sign-on account.
        </Message>
      : null}
      {user?.ssoUser ?
        <Message>
          Your email address was automatically set from your single sign-on
          account.
        </Message>
      : null}

      {emailError ?
        <Message warning>{emailError}</Message>
      : null}
      <Form>
        <Form.Field>
          <label htmlFor="current-email-input">
            {t("users.profile.communication.email.current")}
          </label>
          <Form.Field
            control={Input}
            disabled={
              user.ownerOrganisation?.preventEmailChange || user?.ssoUser
            }
            id="current-email-input"
            style={{ width: 300 }}
            value={user?.emails[0]?.address}
            readOnly
          />
        </Form.Field>
        {user.ownerOrganisation?.preventEmailChange || user?.ssoUser ? null : (
          <>
            <Form.Field>
              <label htmlFor="new-email-input">
                {t("users.profile.communication.email.new")}
              </label>
              <Form.Field
                control={Input}
                id="new-email-input"
                style={{ width: 300 }}
                value={newEmail}
                placeholder={t("placeholder.email")}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNewEmail(e.target.value)
                }
              />
            </Form.Field>
            <Form.Button
              loading={emailLoading}
              primary
              content={t("users.profile.communication.email.change.button")}
              onClick={saveEmail}
            />
          </>
        )}
      </Form>
      <Divider />

      {!user.ssoUser ?
        <>
          <h3>{t("users.account.options.changePassword.title")}</h3>
          <p style={{ marginBottom: 0 }}>
            {t("users.account.options.changePassword.info")}
          </p>
          <PasswordGuidance password={newPassword} />
          <Form>
            <Form.Field>
              <label htmlFor="current-password-input">
                {t("users.account.options.changePassword.currentPass")}
              </label>
              <Form.Field
                control={Input}
                id="current-password-input"
                style={{ width: 300 }}
                type={showPasswordOld ? "text" : "password"}
                value={currentPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCurrentPassword(e.target.value)
                }
                icon={{
                  name: showPasswordOld ? "eye slash" : "eye",
                  circular: true,
                  link: true,
                  onClick: () => setShowPasswordOld(!showPasswordOld),
                }}
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor="new-password-input">
                {t("users.account.options.changePassword.newPass")}
              </label>
              <Form.Field
                control={Input}
                id="new-password-input"
                style={{ width: 300 }}
                type={showPasswordNew ? "text" : "password"}
                value={newPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNewPassword(e.target.value)
                }
                icon={{
                  name: showPasswordNew ? "eye slash" : "eye",
                  circular: true,
                  link: true,
                  onClick: () => setShowPasswordNew(!showPasswordNew),
                }}
              />
            </Form.Field>
          </Form>
          {errorMessage && !working ?
            <Message
              style={{ width: "50%", marginBottom: 0 }}
              size="small"
              negative
            >
              <Icon name="warning circle" />
              {errorMessage}.
            </Message>
          : null}
          <Button
            style={{ marginTop: "15px", maxWidth: "fit-content" }}
            primary
            icon="check"
            content={t("users.account.options.changePassword.change")}
            onClick={() => updatePassword()}
            loading={working}
          />

          <Divider />
          <h3>{t("entry.mfa.title")}</h3>
          <p>{t("entry.mfa.explainer")}</p>
          <Message style={{ marginTop: 0 }}>
            <SuggestedMFAApps />
          </Message>

          <Button
            primary
            style={{ maxWidth: "fit-content" }}
            icon={!mfa ? "lock" : "unlock"}
            content={
              mfa ? t("entry.mfa.disableAction") : t("entry.mfa.enableAction")
            }
            onClick={toggleMfa}
          />

          <EnableMfa
            open={enableMfaModalOpen}
            onClose={() => setEnableMfaModalOpen(false)}
            onEnableMfa={() => setMfa(true)}
          />
          <Divider />
        </>
      : null}

      <h3>Logged-in devices</h3>
      <p>
        A list of all your currently active sessions across devices. You can
        revoke access here, which logs the device out immediately.
      </p>
      {apiTokens.length > 0 ?
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell></Table.HeaderCell>
              <Table.HeaderCell>OS</Table.HeaderCell>
              <Table.HeaderCell>Browser / Device</Table.HeaderCell>
              <Table.HeaderCell>Created</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {apiTokens.map((apiToken: Token, index: number) => {
              const unwrappedToken = (
                (apiToken as Token) ? apiToken : { token: apiToken }) as Token;
              return (
                <Table.Row key={apiToken?.token}>
                  <Table.Cell collapsing>
                    <div>
                      {unwrappedToken.platform === "web" ?
                        <Icon name="computer" size="large" />
                      : null}
                      {unwrappedToken.platform === "mobile" ?
                        <Icon name="mobile alternate" size="large" />
                      : null}
                      {unwrappedToken.platform === "teams" ?
                        <Icon name="microsoft" size="large" />
                      : null}
                      {!unwrappedToken.platform ?
                        <Icon name="question" size="large" />
                      : null}
                    </div>
                  </Table.Cell>
                  <Table.Cell collapsing>
                    {unwrappedToken.os || "Unknown"}
                  </Table.Cell>
                  <Table.Cell collapsing>
                    {unwrappedToken.client || "Unknown"}
                  </Table.Cell>
                  <Table.Cell collapsing>
                    {unwrappedToken.createdAt ?
                      moment(unwrappedToken.createdAt).format(
                        "DD/MM/YYYY HH:mm",
                      )
                    : "Unknown"}
                  </Table.Cell>
                  <Table.Cell collapsing textAlign="right">
                    {unwrappedToken.token === api.token ?
                      <Label>This device</Label>
                    : null}
                    <Button
                      basic
                      size="mini"
                      content="Log out"
                      onClick={() => {
                        logoutToken(unwrappedToken.id!, index);
                      }}
                      loading={loggingOutToken === unwrappedToken.id}
                      disabled={unwrappedToken.token === api.token}
                    />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      : null}

      <h3>{t("users.account.options.logOut.title")}</h3>
      <p>{t("users.account.options.logOut.info")}</p>
      <Button
        basic
        icon="sign out"
        content={t("users.account.options.logOut.button")}
        onClick={logoutOthers}
        loading={loggingOutOthers}
      />

      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={disableMfaModel}
      >
        <Modal.Header>
          <Icon name="lock" style={{ paddingRight: "5px" }} />
          {t("entry.mfa.disableAction")}
        </Modal.Header>
        <ModalContent>
          {user.ownerOrganisation?.mfaEnforcing ?
            <Message>
              {t("entry.mfa.disabledWhenEnforcedMessage", {
                orgName: user.ownerOrganisation?.name,
              })}
            </Message>
          : null}
          <p>{t("entry.mfa.reenableSecurityMessage")}</p>
          <p>{t("entry.mfa.requirePassword")}</p>
          <div style={{ textAlign: "center" }}>
            <Input
              size="large"
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.keyCode === 13) disableMfa();
              }}
              type="password"
              onChange={(e) => setMfaPassword(e.target.value)}
              value={mfaPassword}
              autoFocus
            />
          </div>
          <Message info>
            When you disable multi-factor authentication, your recovery codes
            will be invalidated and you will no longer be able to use them to
            access your account. If you re-enable multi-factor authentication,
            you will receive new recovery codes.
          </Message>
        </ModalContent>
        <ModalActions>
          <Button
            content={t("generic.cancel")}
            onClick={() => setDisableMfaModel(false)}
          />
          <Button
            primary
            content={t("generic.verify")}
            onClick={() => disableMfa()}
          />
        </ModalActions>
      </Modal>
    </Container>
  );
};

export default UserSettingsSecurity;
